import React, { useState, useMemo } from 'react';
import { X, Search, Shield, Eye, Plus, Check } from 'lucide-react';
import { Control } from '../types';
import { useAppContext } from '../contexts/AppContext';

interface ControlSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (selectedControlsString: string) => void;
    type: 'prevention' | 'detection';
    existingValue: string;
    availableControls: Control[];
}

export const ControlSelectionModal: React.FC<ControlSelectionModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    type,
    existingValue,
    availableControls = []
}) => {
    const { navigate } = useAppContext();
    const [searchTerm, setSearchTerm] = useState('');
    // Parse existing CSV string into a Set of names for easy lookup
    const [selectedNames, setSelectedNames] = useState<Set<string>>(() => {
        const set = new Set<string>();
        if (existingValue) {
            existingValue.split(';').map(s => s.trim()).filter(Boolean).forEach(s => set.add(s));
        }
        return set;
    });

    // Reset state when opening/closing or changing initial value would be better handled by a useEffect
    // but for now we rely on the parent mounting/unmounting or re-rendering logic if key changes.
    // Actually, let's allow updating if existingValue prop changes while open (rare but safe):
    React.useEffect(() => {
        const set = new Set<string>();
        if (existingValue) {
            existingValue.split(';').map(s => s.trim()).filter(Boolean).forEach(s => set.add(s));
        }
        setSelectedNames(set);
    }, [existingValue, isOpen]);

    const filteredControls = useMemo(() => {
        return availableControls.filter(c =>
            c.type === type &&
            (c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.description?.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [availableControls, type, searchTerm]);

    const toggleSelection = (name: string) => {
        const newSet = new Set(selectedNames);
        if (newSet.has(name)) {
            newSet.delete(name);
        } else {
            newSet.add(name);
        }
        setSelectedNames(newSet);
    };

    const handleApply = () => {
        // Join with semicolons
        const result = Array.from(selectedNames).join('; ');
        onSelect(result);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[80vh] animate-in fade-in zoom-in duration-200">

                {/* Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-slate-900/50 rounded-t-xl">
                    <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${type === 'prevention' ? 'bg-emerald-100 text-emerald-600' : 'bg-purple-100 text-purple-600'}`}>
                            {type === 'prevention' ? <Shield size={20} /> : <Eye size={20} />}
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                Select {type === 'prevention' ? 'Prevention' : 'Detection'} Controls
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Choose from the centralized Controls Library or add custom text.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                {/* Search */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            type="text"
                            placeholder={`Search ${type} controls...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {filteredControls.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No matching controls found in library.</p>
                            <button
                                onClick={() => {
                                    onClose();
                                    navigate('/controls-manager');
                                }}
                                className="mt-2 text-blue-500 hover:underline text-sm flex items-center justify-center gap-1 mx-auto"
                            >
                                <Plus size={14} /> Create New in Library
                            </button>
                        </div>
                    ) : (
                        filteredControls.map(control => (
                            <div
                                key={control.id}
                                onClick={() => toggleSelection(control.name)}
                                className={`
                  flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all
                  ${selectedNames.has(control.name)
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-slate-700'}
                `}
                            >
                                <div className={`mt-1 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0
                  ${selectedNames.has(control.name) ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300 dark:border-gray-600'}
                `}>
                                    {selectedNames.has(control.name) && <Check size={12} />}
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{control.name}</h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{control.description}</p>
                                    {control.effectiveness && (
                                        <span className={`
                      inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full border
                      ${control.effectiveness === 'High' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                                control.effectiveness === 'Medium' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-red-50 text-red-600 border-red-200'}
                    `}>
                                            {control.effectiveness} Effectiveness
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))
                    )}

                    {/* Custom Text Option (if user typed something not in list, maybe show logic here? 
              For now keeping it simple: The search works on the library. 
              The OUTPUT is string, so they can manually type in the editor too.)
          */}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-900/50 rounded-b-xl flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                        {selectedNames.size} controls selected
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white">
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg shadow-sm transition-colors"
                        >
                            Apply Selection
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};
